import { isFutureDateString, isISODateString } from '@anwb/poncho/utilities/date-utils';
import {
	Formatter,
	formatDateStringToYear,
	formatFirstCaseToUpper,
	formatNumber,
} from '@anwb/poncho/utilities/formatters';

/**
 * Maps the input bodyShape to the formatted bodyShape
 * @param bodyShape uppercase bodyShape*
 * @param fallback the value to return if bodyShape is undefined.
 * @example
 * // returns 'Hatchback'
 * formatBodyShape('HATCHBACK');
 */
export const formatBodyShape: Formatter<string, string> = (
	bodyShape?: string,
	{ fallback = null } = { fallback: null }
) => {
	if (bodyShape === undefined) return fallback;

	switch (bodyShape) {
		case 'MPV':
			return 'MPV';
		case 'SUV':
			return 'SUV';
		case 'COUPE':
			return 'Coupé';
		default:
			return formatFirstCaseToUpper(bodyShape);
	}
};

/**
 * Formats a compression value to append :1
 * @param value compression value
 * @param fallback the value to return if value is undefined.
 * @example
 * // returns '1.9:1'
 * formatCompressionRatio(1.89);
 * @example
 * // returns '1:1'
 * formatCompressionRatio(1.00);
 */
export const formatCompressionRatio: Formatter<number, string> = (
	value?: number,
	{ fallback = null } = {}
) =>
	value
		? formatNumber(value, {
				suffix: ':1',
				decimals: 1,
				hideEmptyDecimals: true,
			})
		: fallback;

/**
 * Formats consumption value from x/100km to 1 on x
 * @param consumption consumption in litres
 * @param fallback the value to return if consumption is undefined.
 * @example
 * // returns 'ca. 1 op 20'
 * formatConsumption(5);
 */
export const formatConsumption: Formatter<number, string> = (
	consumption?: number,
	{ fallback = null } = {}
) =>
	consumption === undefined
		? fallback
		: formatNumber(Math.round(100 / consumption), { prefix: 'ca. 1 op ' });

/**
 * Appends -deurs to the door count
 * @param value amount of doors
 * @example
 * // returns '4-deurs'
 * formatDoorCount(4);
 */
export const formatDoorCount: Formatter<number, string> = (value) =>
	formatNumber(value, { suffix: '-deurs' });

/**
 * Takes the amount of doors and maps it into a range
 * @param numberOfDoors amount of doors
 * @param fallback the value to return if numberOfDoors is not supported.
 * @example
 * // returns '2-3'
 * formatDoorCountRange(2)
 */
export const formatDoorCountRange: Formatter<number, string> = (
	numberOfDoors,
	{ fallback = null } = { fallback: null }
) => {
	switch (numberOfDoors) {
		case 2:
			return '2-3';

		case 3:
			return '2-3';

		case 4:
			return '4-5';

		case 5:
			return '4-5';

		default:
			return fallback;
	}
};

/**
 * Takes fuel per 100km and shows it with consumption appended
 * @param efficiency fuel per 100km
 * @param fallback the value to return if efficiency is undefined or 0.
 * @example
 * // returns '5,0 l/100km (ca. 1 op 20)'
 * formatFuelEfficiency(5)
 */
export const formatFuelEfficiency: Formatter<number, string> = (
	efficiency,
	{ fallback = null } = { fallback: null }
) => {
	if (efficiency === undefined || efficiency === 0) return fallback;

	return formatNumber(efficiency, {
		decimals: 1,
		suffix: ` l/100km (${formatConsumption(efficiency)})`,
	});
};

/**
 * Takes electricity per 100km and shows it
 * @param efficiency electricity per 100km
 * @param fallback the value to return if efficiency is undefined or 0.
 * @example
 * // returns '17,0 kWh/100km'
 * formatFuelEfficiencyEV(17)
 */
export const formatFuelEfficiencyEV: Formatter<number, string> = (
	efficiency,
	{ fallback = null } = { fallback: null }
) => {
	if (efficiency === undefined || efficiency === 0) return fallback;

	return formatNumber(efficiency, {
		decimals: 1,
		suffix: ' kWh/100km',
	});
};

/**
 * Takes usage in kilogram per 100km and shows it
 * @param efficiency fuelcell per 100km
 * @param fallback the value to return if efficiency is undefined or 0.
 * @example
 * // returns '1,0 kg/100km'
 * formatFuelEfficiencyFCEV(1)
 */
export const formatFuelEfficiencyFCEV: Formatter<number, string> = (
	efficiency,
	{ fallback = null } = { fallback: null }
) => {
	if (efficiency === undefined || efficiency === 0) return fallback;

	return formatNumber(efficiency, {
		decimals: 1,
		suffix: ' kg/100km',
	});
};

/**
 * Takes the fuel and formats it into a prettier string
 * @param fuelType fuel used by car
 * @param fallback the value to return if fuelType is undefined
 * @example
 * // returns 'Waterstof'
 * formatFuelType('WATERSTOF')
 */
export const formatFuelType: Formatter<string, string> = (
	fuelType,
	{ fallback = null } = { fallback: null }
) => {
	if (fuelType === undefined) return fallback;

	switch (fuelType) {
		case 'LPG':
			return 'LPG';
		case 'CNG':
			return 'CNG';
		default:
			return formatFirstCaseToUpper(fuelType);
	}
};

type FormatPowerInKilowattOptions = {
	horsePower?: number;
};
/**
 * Takes the charge and power and combines it into a formatted string
 * @param charge charge of the battery
 * @param fallback the value to return if charge is undefined or 0
 * @param horsePower power of the motor
 *
 * @example
 * // returns '147 kW (200 pk)'
 * formatPowerInKilowatt(147, {horsePower: 200, fallback: '-'} )
 * @example
 * // returns '147 kW'
 * formatPowerInKilowatt(147)
 */
export const formatPowerInKilowatt: Formatter<number, string, FormatPowerInKilowattOptions> = (
	charge,
	{ fallback = null, horsePower } = { fallback: null, horsePower: undefined }
) => {
	if (charge === undefined || charge === 0) return fallback;

	const suffix = horsePower ? ` kW (${horsePower} pk)` : ' kW';

	return formatNumber(charge, {
		decimals: 0,
		suffix,
	});
};

type FormatProductionPeriodOptions = {
	from?: string;
	until?: string;
	shorten?: boolean;
	defaultFromValue?: string;
};

/**
 * Maps two dates into a production period like '2016 - nu'
 * @param {Object} param the object containing the production period data
 * @param {string} param.from containing the starting production date
 * @param {string} param.until containing ending production date
 * @param {boolean} param.shorten true when to shorten year to two digits
 * @param {string} param.defaultFromValue the default fallback value if from is undefined
 * @example
 * // returns '2002 - 2010'
 * formatProductionPeriod({from: '2002-01-01',until: '2010-01-01'})
 * @example
 * // returns "'02 - '10"
 * formatProductionPeriod({from: '2002-01-01',until: '2010-01-01',shorten: true})
 * @example
 * // returns '2002 - nu'
 * formatProductionPeriod({from: '2002-01-01',until: '9999-01-01'})
 * formatProductionPeriod({from: '2002-01-01'})
 * formatProductionPeriod({from: '2002-01-01',until: ''})
 */
export const formatProductionPeriod = ({
	from,
	until,
	shorten,
	defaultFromValue = '',
}: FormatProductionPeriodOptions): string => {
	let fromYearStr: string;
	let untilYearStr: string;

	if (!from || (from.length > 4 && !isISODateString(from))) {
		fromYearStr = defaultFromValue;
	} else {
		const fromYear = formatDateStringToYear(from);
		fromYearStr = fromYear ? fromYear.toString() : defaultFromValue;
	}

	if (!until || (until.length > 4 && !isISODateString(until))) {
		untilYearStr = 'nu';
	} else if (isFutureDateString(until)) {
		untilYearStr = 'nu';
	} else {
		const untilYear = formatDateStringToYear(until);
		untilYearStr = untilYear ? untilYear.toString() : 'nu';
	}

	if (shorten) {
		if (fromYearStr) fromYearStr = `'${fromYearStr.slice(-2)}`;
		if (untilYearStr !== 'nu') untilYearStr = `'${untilYearStr.slice(-2)}`;
	}

	return `${fromYearStr} - ${untilYearStr}`;
};
